import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, Loading } from "@/components/common/ui";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CreatableCombobox } from "@/components/CreatableCombobox";
import { toast } from "sonner";
import { Search, GitMerge, Tags, ArrowRight, X, Database } from "lucide-react";

// Fields that can be mass-renamed / merged across ALL spends (mirrors the
// backend _RECAT_FIELDS map). Every one is a real category dimension on a spend.
const FIELDS = [
  { value: "vertical", label: "Verticals" },
  { value: "cost_type", label: "Cost Types" },
  { value: "activity", label: "Activities" },
  { value: "outcome", label: "Outcomes" },
  { value: "platform", label: "Platforms" },
  { value: "vendor", label: "Vendors" },
  { value: "vis_perf", label: "Visibility / Performance" },
  { value: "collateral_type", label: "Collateral Types" },
  { value: "billing_business", label: "Billing Businesses" },
  { value: "sub_line_item", label: "Sub-Line Items" },
];

export function CategoryManager() {
  const { money } = useCurrency();
  const [field, setField] = useState("vertical");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [target, setTarget] = useState("");
  const [applying, setApplying] = useState(false);

  const load = async (f) => {
    setLoading(true);
    try {
      const d = await api.spendCategoryValues(f);
      setData(d);
    } catch (e) {
      toast.error("Failed to load category values");
      setData({ values: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelected([]); setTarget(""); setQuery("");
    load(field);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field]);

  const values = data?.values || [];
  const q = query.trim().toLowerCase();
  const filtered = q ? values.filter((v) => String(v.value).toLowerCase().includes(q)) : values;

  const selInfo = useMemo(() => {
    const sel = values.filter((v) => selected.includes(v.value));
    return {
      count: sel.length,
      lines: sel.reduce((s, v) => s + (v.count || 0), 0),
      total: sel.reduce((s, v) => s + (v.total || 0), 0),
    };
  }, [values, selected]);

  const toggle = (v) => setSelected((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const allFilteredSelected = filtered.length > 0 && filtered.every((v) => selected.includes(v.value));
  const toggleAll = () =>
    setSelected((s) => (allFilteredSelected ? s.filter((x) => !filtered.some((v) => v.value === x)) : Array.from(new Set([...s, ...filtered.map((v) => v.value)]))));

  const targetOptions = useMemo(
    () => values.filter((v) => v.value !== "(blank)").map((v) => ({ value: v.value, label: v.value })),
    [values]
  );

  const isMerge = selInfo.count > 1;
  const fieldLabel = FIELDS.find((f) => f.value === field)?.label || field;

  const apply = async () => {
    const to = (target || "").trim();
    if (!selected.length) { toast.error("Select at least one value to rename or merge"); return; }
    if (!to) { toast.error("Choose or type the target value"); return; }
    setApplying(true);
    try {
      const res = await api.recategorizeSpends(field, selected, to);
      toast.success(`${res.modified} spend row${res.modified === 1 ? "" : "s"} updated \u2192 "${to}"`);
      setSelected([]); setTarget("");
      await load(field);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update categories");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="category-manager">
      <div className="flex items-start gap-2 rounded-lg border bg-[hsl(var(--primary)/0.06)] px-3 py-2 text-xs text-muted-foreground" data-testid="category-manager-note">
        <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>
          Clean up your taxonomy in one place. Pick a category, then <b className="text-foreground">rename</b> a single value or
          <b className="text-foreground"> merge</b> several values into one — the change is applied across <b className="text-foreground">every spend</b> instantly and the master list is kept in sync.
        </span>
      </div>

      <SectionCard
        title="Category Manager"
        description="Mass-rename or merge category values across all spend records."
        testId="category-manager-card"
        action={
          <div className="flex items-center gap-2">
            <Select value={field} onValueChange={setField}>
              <SelectTrigger className="h-9 w-52" data-testid="category-field-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELDS.map((f) => <SelectItem key={f.value} value={f.value} data-testid={`category-field-${f.value}`}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Badge variant="secondary" className="tabular-nums" data-testid="category-values-count">{values.length}</Badge>
          </div>
        }
      >
        <div className="p-4">
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${fieldLabel.toLowerCase()}\u2026`} className="h-9 pl-8" data-testid="category-search" />
          </div>

          {loading ? (
            <Loading rows={5} />
          ) : values.length === 0 ? (
            <div className="rounded-lg border bg-[hsl(var(--surface-2))] p-6 text-center text-sm text-muted-foreground" data-testid="category-empty">No values found for this category.</div>
          ) : (
            <div className="overflow-hidden rounded-lg border" data-testid="category-values-list">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b bg-[hsl(var(--surface-2))]">
                    <th className="w-10 px-3 py-2 text-left">
                      <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="category-select-all" />
                    </th>
                    <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Value</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Lines</th>
                    <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Spend</th>
                  </tr>
                </thead>
                <tbody className="max-h-96">
                  {filtered.map((v, i) => {
                    const isSel = selected.includes(v.value);
                    const blank = v.value === "(blank)";
                    return (
                      <tr
                        key={v.value}
                        onClick={() => toggle(v.value)}
                        className={`cursor-pointer border-b last:border-0 transition-colors duration-150 ${isSel ? "bg-[hsl(var(--primary)/0.08)]" : i % 2 ? "row-zebra" : ""} hover:bg-[hsl(var(--primary)/0.05)]`}
                        data-testid={`category-row-${i}`}
                      >
                        <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={isSel} onCheckedChange={() => toggle(v.value)} aria-label={`Select ${v.value}`} data-testid={`category-check-${i}`} />
                        </td>
                        <td className="px-3 py-2.5 text-sm font-medium">
                          {blank ? <span className="italic text-muted-foreground">(blank)</span> : v.value}
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs tabular-nums text-muted-foreground">{v.count}</td>
                        <td className="num px-3 py-2.5 text-right text-sm tabular-nums">{money(v.total)}</td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-muted-foreground">No values match your search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Action bar — appears once one or more values are selected */}
        {selInfo.count > 0 && (
          <div className="sticky bottom-0 border-t bg-card/95 p-4 backdrop-blur" data-testid="category-action-bar">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary)/0.12)] px-2.5 py-1 text-xs font-medium text-[hsl(var(--primary))]">
                  {isMerge ? <GitMerge className="h-3.5 w-3.5" /> : <Tags className="h-3.5 w-3.5" />}
                  {isMerge ? `Merge ${selInfo.count} values` : "Rename value"}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">{selInfo.lines} line{selInfo.lines === 1 ? "" : "s"} · {money(selInfo.total)}</span>
                <button onClick={() => setSelected([])} className="inline-flex items-center gap-0.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" data-testid="category-clear-selection">
                  <X className="h-3 w-3" /> clear
                </button>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex items-center gap-2">
                  <span className="hidden text-xs text-muted-foreground sm:inline">into</span>
                  <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" />
                  <div className="w-64">
                    <CreatableCombobox
                      value={target}
                      options={targetOptions}
                      placeholder={"Target value\u2026"}
                      searchPlaceholder={"Search or type a new value\u2026"}
                      createLabel="Create new"
                      onValueChange={setTarget}
                      onCreate={setTarget}
                      testId="category-target-combobox"
                    />
                  </div>
                </div>
                <Button onClick={apply} disabled={applying || !target.trim()} data-testid="category-apply-button">
                  {isMerge ? <GitMerge className="mr-1.5 h-4 w-4" /> : <Tags className="mr-1.5 h-4 w-4" />}
                  {applying ? "Applying\u2026" : isMerge ? "Merge" : "Rename"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
